"use client";

import { useLayoutEffect, useRef, type ReactNode } from "react";
import { Typography } from "@cappelendamm/design-system/react";

// Mirrors the `sm` breakpoint (768px) in the design system, where the CMS size takes over.
const MOBILE_QUERY = "(max-width: 767px)";
const MIN_PX = 28;
const MAX_PX = 48;

export function FitTitle({
  as,
  className,
  children,
}: {
  as: "h1" | "h2";
  className?: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLHeadingElement>(null);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;

    const mobile = window.matchMedia(MOBILE_QUERY);

    // Largest font size on mobile where the longest word still fits the page container.
    const fit = () => {
      element.style.fontSize = "";
      if (!mobile.matches) return;
      const container =
        element.closest(".cvr-container") ?? element.parentElement;
      const available = container?.clientWidth ?? element.clientWidth;
      const style = getComputedStyle(element);
      const context = document.createElement("canvas").getContext("2d");
      if (!context) return;
      const words = (element.textContent ?? "").split(/\s+/).filter(Boolean);
      for (let px = MAX_PX; px > MIN_PX; px--) {
        context.font = `${style.fontStyle} ${style.fontWeight} ${px}px ${style.fontFamily}`;
        const widest = Math.max(
          ...words.map((word) => context.measureText(word).width),
        );
        if (widest <= available) {
          element.style.fontSize = `${px}px`;
          return;
        }
      }
      element.style.fontSize = `${MIN_PX}px`;
    };

    fit();
    let active = true;
    document.fonts.ready.then(() => {
      if (active) fit();
    });

    let lastWidth = element.clientWidth;
    const observer = new ResizeObserver(() => {
      if (element.clientWidth === lastWidth) return;
      lastWidth = element.clientWidth;
      fit();
    });
    observer.observe(element);
    mobile.addEventListener("change", fit);

    return () => {
      active = false;
      observer.disconnect();
      mobile.removeEventListener("change", fit);
    };
  }, []);

  return (
    <Typography as={as} variant="title-xl" className={className} ref={ref}>
      {children}
    </Typography>
  );
}
